import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton,
  MenuItem, Select, Snackbar, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { api } from '../api/client';
import type {
  GroupDetail, GroupSummary, ManagedUser, RightCatalogueEntry,
} from '../api/types';
import { useAuthStore } from '../stores/auth';
import { Roles } from '../auth/roles';
import { formatDateTime } from '../utils/dateFormat';
import { makeDelegationGuard } from '../auth/delegation';

/**
 * Groups admin: list / create / edit / delete groups, manage members + rights.
 *
 * Backend semantics:
 * - SP sees all groups; Admin sees only groups they're a member of.
 * - Create / delete are SP-only.
 * - Rename, manage members, manage rights are gated by rights — current
 *   defaults make these Admin-permitted as long as the admin belongs to the group.
 */
export function GroupsPage() {
  const isSp = useAuthStore((s) => s.isServiceProvider)();
  const hasRole = useAuthStore((s) => s.hasRole);
  const callerRights = useAuthStore((s) => s.user?.effectiveRights);
  const isAdminService = hasRole(Roles.AdminService);
  const unbounded = isSp || isAdminService;

  const [groups, setGroups] = useState<GroupSummary[]>([]);
  const [catalogue, setCatalogue] = useState<RightCatalogueEntry[]>([]);
  const [allUsers, setAllUsers] = useState<ManagedUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  // Drawer-style detail view: load full group on demand when admin clicks Edit.
  const [detail, setDetail] = useState<GroupDetail | null>(null);
  const [rightsDraft, setRightsDraft] = useState<Set<string>>(new Set());
  const [rightsDialog, setRightsDialog] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');

  const [renameOpen, setRenameOpen] = useState(false);
  const [renameName, setRenameName] = useState('');
  const [renameDescription, setRenameDescription] = useState('');

  const [addMemberId, setAddMemberId] = useState<string>('');
  const [confirmDelete, setConfirmDelete] = useState<GroupSummary | null>(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [groupsRes, rightsRes, usersRes] = await Promise.all([
        api.get<{ groups: GroupSummary[] }>('/api/groups/'),
        api.get<{ rights: RightCatalogueEntry[] }>('/api/rights/'),
        api.get<{ users: ManagedUser[] }>('/api/users/'),
      ]);
      setGroups(groupsRes.data?.groups ?? []);
      setCatalogue(rightsRes.data?.rights ?? []);
      setAllUsers(usersRes.data?.users ?? []);
    } catch (ex) {
      setError((ex as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const featureKeys = useMemo(() => {
    const set = new Set<string>();
    for (const r of catalogue) set.add(r.featureKey);
    return Array.from(set).sort();
  }, [catalogue]);

  // Mirrors the server's GrantDelegation ceiling — greys out rights a bounded
  // admin can't delegate. Unbounded for SP / AdminService.
  const canDelegate = useMemo(
    () => makeDelegationGuard(catalogue, callerRights, unbounded),
    [catalogue, callerRights, unbounded],
  );

  const openDetail = async (g: GroupSummary) => {
    try {
      const { data } = await api.get<GroupDetail>(`/api/groups/${g.id}`);
      setDetail(data);
      setRenameName(data.name);
      setRenameDescription(data.description ?? '');
    } catch (ex) {
      setError((ex as Error).message);
    }
  };

  const createGroup = async () => {
    if (!createName.trim()) return;
    try {
      await api.post('/api/groups/', { name: createName.trim(), description: createDescription.trim() || null });
      setSnackbar(`Created group "${createName.trim()}"`);
      setCreateOpen(false); setCreateName(''); setCreateDescription('');
      reload();
    } catch (ex) {
      setError((ex as Error).message);
    }
  };

  const renameGroup = async () => {
    if (!detail) return;
    try {
      await api.patch(`/api/groups/${detail.id}`, {
        name: renameName.trim(),
        description: renameDescription.trim() || null,
      });
      setSnackbar(`Updated "${renameName.trim()}"`);
      setRenameOpen(false);
      const refreshed = await api.get<GroupDetail>(`/api/groups/${detail.id}`);
      setDetail(refreshed.data);
      reload();
    } catch (ex) {
      setError((ex as Error).message);
    }
  };

  const deleteGroup = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/api/groups/${confirmDelete.id}`);
      setSnackbar(`Deleted "${confirmDelete.name}"`);
      setConfirmDelete(null);
      if (detail?.id === confirmDelete.id) setDetail(null);
      reload();
    } catch (ex) {
      setError((ex as Error).message);
    }
  };

  const openRightsDialog = () => {
    if (!detail) return;
    setRightsDraft(new Set(detail.rights));
    setRightsDialog(true);
  };

  const toggleRight = (key: string) => {
    setRightsDraft((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const saveRights = async () => {
    if (!detail) return;
    try {
      await api.put(`/api/groups/${detail.id}/rights`, { rights: Array.from(rightsDraft) });
      setSnackbar('Group rights updated');
      setRightsDialog(false);
      const refreshed = await api.get<GroupDetail>(`/api/groups/${detail.id}`);
      setDetail(refreshed.data);
      reload();
    } catch (ex) {
      setError((ex as Error).message);
    }
  };

  const addMember = async () => {
    if (!detail || !addMemberId) return;
    try {
      await api.post(`/api/groups/${detail.id}/users`, { userId: addMemberId });
      setSnackbar('Member added');
      setAddMemberId('');
      const refreshed = await api.get<GroupDetail>(`/api/groups/${detail.id}`);
      setDetail(refreshed.data);
      reload();
    } catch (ex) {
      setError((ex as Error).message);
    }
  };

  const removeMember = async (userId: string) => {
    if (!detail) return;
    try {
      await api.delete(`/api/groups/${detail.id}/users/${userId}`);
      setSnackbar('Member removed');
      const refreshed = await api.get<GroupDetail>(`/api/groups/${detail.id}`);
      setDetail(refreshed.data);
      reload();
    } catch (ex) {
      setError((ex as Error).message);
    }
  };

  const candidateMembers = useMemo(() => {
    if (!detail) return allUsers;
    const memberIds = new Set(detail.members.map((m) => m.id));
    return allUsers.filter((u) => !memberIds.has(u.id));
  }, [detail, allUsers]);

  return (
    <Stack spacing={2}>
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>Groups</Typography>
            <Button size="small" startIcon={<RefreshIcon />} onClick={reload}>Refresh</Button>
            {isSp && (
              <Button variant="contained" sx={{ ml: 1 }} startIcon={<GroupAddIcon />}
                      onClick={() => setCreateOpen(true)}>
                Create group
              </Button>
            )}
          </Stack>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {loading ? <CircularProgress size={20} /> : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Members</TableCell>
                  <TableCell>Rights</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map((g) => (
                  <TableRow key={g.id} hover selected={detail?.id === g.id}>
                    <TableCell sx={{ fontWeight: 600 }}>{g.name}</TableCell>
                    <TableCell>{g.description ?? <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>}</TableCell>
                    <TableCell align="right">{g.userCount}</TableCell>
                    <TableCell>
                      {g.rights.length === 0 ? (
                        <Box component="span" sx={{ color: 'text.disabled' }}>none</Box>
                      ) : (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {g.rights.slice(0, 4).map((k) => (
                            <Chip key={k} label={k} size="small" sx={{ fontFamily: 'monospace' }} />
                          ))}
                          {g.rights.length > 4 && (
                            <Chip label={`+${g.rights.length - 4}`} size="small" variant="outlined" />
                          )}
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Manage">
                        <IconButton size="small" onClick={() => openDetail(g)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {isSp && (
                        <Tooltip title="Delete (cascades members + rights)">
                          <IconButton size="small" color="error" onClick={() => setConfirmDelete(g)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {detail && (
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                {detail.name}{' '}
                <Typography component="span" variant="body2" color="text.secondary">
                  ({detail.id})
                </Typography>
              </Typography>
              <Button size="small" startIcon={<EditIcon />} onClick={() => setRenameOpen(true)}>
                Rename / edit
              </Button>
              <Button size="small" sx={{ ml: 1 }} startIcon={<VpnKeyIcon />} onClick={openRightsDialog}>
                Manage rights
              </Button>
            </Stack>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Rights ({detail.rights.length})
            </Typography>
            <Box sx={{ mb: 2 }}>
              {detail.rights.length === 0 ? (
                <Typography variant="body2" color="text.secondary">none granted</Typography>
              ) : (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {detail.rights.map((k) => (
                    <Chip key={k} label={k} size="small" sx={{ fontFamily: 'monospace' }} />
                  ))}
                </Stack>
              )}
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Members ({detail.members.length})
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Select size="small" value={addMemberId} onChange={(e) => setAddMemberId(e.target.value)}
                      displayEmpty sx={{ minWidth: 240 }}>
                <MenuItem value="">— pick a user to add —</MenuItem>
                {candidateMembers.map((u) => (
                  <MenuItem key={u.id} value={u.id}>{u.username} ({u.email})</MenuItem>
                ))}
              </Select>
              <Button variant="contained" startIcon={<PersonAddIcon />}
                      disabled={!addMemberId} onClick={addMember}>
                Add
              </Button>
            </Stack>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Added</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {detail.members.map((m) => (
                  <TableRow key={m.id} hover>
                    <TableCell>{m.username}</TableCell>
                    <TableCell>{m.email}</TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(m.addedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Remove from group">
                        <IconButton size="small" color="error" onClick={() => removeMember(m.id)}>
                          <PersonRemoveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Create group */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)}>
        <DialogTitle>Create group</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label="Name" fullWidth required
                     value={createName} onChange={(e) => setCreateName(e.target.value)} />
          <TextField margin="dense" label="Description" fullWidth multiline rows={2}
                     value={createDescription} onChange={(e) => setCreateDescription(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={createGroup} disabled={!createName.trim()}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Rename / edit description */}
      <Dialog open={renameOpen} onClose={() => setRenameOpen(false)}>
        <DialogTitle>Edit group</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label="Name" fullWidth required
                     value={renameName} onChange={(e) => setRenameName(e.target.value)} />
          <TextField margin="dense" label="Description" fullWidth multiline rows={2}
                     value={renameDescription} onChange={(e) => setRenameDescription(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={renameGroup} disabled={!renameName.trim()}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete group?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{confirmDelete?.name}</strong>? This removes the group and cascades
            its memberships and rights. Users keep their other group memberships.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={deleteGroup}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Manage rights */}
      <Dialog open={rightsDialog} onClose={() => setRightsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Group rights — {detail?.name}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select rights or feature wildcards (<code>feature.*</code>). The global{' '}
            <code>*</code> wildcard is SP-only — non-SP requests including it are rejected.
          </Typography>
          {!unbounded && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You can only add or remove rights you hold yourself. Other rights stay
              checked but locked — the server preserves them on save.
            </Alert>
          )}
          {featureKeys.map((feature) => {
            const wildcardKey = `${feature}.*`;
            const wildcardSelected = rightsDraft.has(wildcardKey);
            const wildcardDelegable = canDelegate(wildcardKey);
            const concreteKeys = catalogue.filter((c) => c.featureKey === feature);
            return (
              <Box key={feature} sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mr: 2 }}>{feature}</Typography>
                  <FormControlLabel
                    control={<Checkbox size="small" checked={wildcardSelected}
                                       disabled={!wildcardDelegable}
                                       onChange={() => toggleRight(wildcardKey)} />}
                    label={<code>{wildcardKey}</code>}
                  />
                </Stack>
                <Box sx={{ pl: 3 }}>
                  {concreteKeys.map((r) => {
                    const concreteDelegable = canDelegate(r.key);
                    return (
                      <FormControlLabel key={r.key}
                        control={
                          <Checkbox size="small"
                                    checked={wildcardSelected || rightsDraft.has(r.key)}
                                    disabled={wildcardSelected || !concreteDelegable}
                                    onChange={() => toggleRight(r.key)} />
                        }
                        label={
                          <Box>
                            <code>{r.key}</code>{' '}
                            {r.description && (
                              <Typography component="span" variant="caption" color="text.secondary">
                                — {r.description}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    );
                  })}
                </Box>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRightsDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveRights}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar(null)}
                message={snackbar ?? ''} />
    </Stack>
  );
}
